import React, { useEffect, useState } from "react";

import { CHAIN_ID, SupportedCollaterals } from "@astrid-dao/lib-base";
import { getDeploymentConstantsFor } from "@astrid-dao/lib-ethers";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { AbstractConnector } from "@web3-react/abstract-connector";
import { Button, Text, Flex, Link, Box, Heading } from "theme-ui";
import { useAccount, useNetwork } from "wagmi";

import { BaseProp } from "../@types/base-types";
import {
  USE_TESTNET,
  astridDaoFrontendConfigProxy,
  collateralTypesForLocal,
  getShibuyaChainIDMaybeLocal,
  isLocalDevelopmentMode
} from "../config";
import { updateGasEstimatorChainId } from "../gasEstimator";
import { Icon } from "./Icon";

export type ConnectionState =
  | { type: "inactive" }
  | {
      type: "activating" | "active" | "rejectedByUser" | "alreadyPending" | "failed";
      connector: AbstractConnector;
    };

export type ConnectionAction =
  | { type: "startActivating"; connector: AbstractConnector }
  | { type: "fail"; error: Error }
  | { type: "finishActivating" | "retry" | "cancel" | "deactivate" };

type WalletConnectorProps = { loader?: React.ReactNode } & BaseProp;

export const WalletConnector: React.FC<WalletConnectorProps> = ({ children, loader }) => {
  const { isConnected, isConnecting, isReconnecting } = useAccount();
  const { chain, chains } = useNetwork();

  const isNetworkSupported = chains.find(item => item.id === chain?.id) ? true : false;

  useEffect(() => {
    updateGasEstimatorChainId(chain?.id ?? CHAIN_ID.astar);
  }, [chain?.id]);

  if (isConnecting || isReconnecting) {
    return <>{loader}</>;
  }

  if (isNetworkSupported && isConnected) {
    return <>{children}</>;
  }

  return (
    <Flex
      sx={{
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
        bg: "W100",
        padding: "0px 12px"
      }}
    >
      <Flex
        sx={{
          flexDirection: "column",
          alignItems: "center",
          gap: 3
        }}
      >
        <Flex
          sx={{
            flexDirection: "column",
            justifyConent: "center",
            alignItems: "center",
            gap: 3
          }}
        >
          <Text variant="text.msTitle24" sx={{ color: "B100" }}>
            Connect wallet
          </Text>
        </Flex>

        <Flex
          sx={{
            mt: "24px",
            width: "100%",
            maxWidth: "540px",
            gap: "40px",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Text
            variant="text.mediumH3"
            sx={{
              color: "B80",
              textAlign: "center"
            }}
          >
            By connecting your wallet, you agree that you have acknowledged{" "}
            <Link
              className="external-nav-link"
              href="https://astriddao.gitbook.io/documentation"
              target="_blank"
              rel="noopener noreferrer"
            >
              how AstridDAO works, and the risks it might involve.
            </Link>{" "}
            DYOR and use it at your own risk.
          </Text>

          <ConnectButton.Custom>
            {({
              account,
              chain,
              openAccountModal,
              openChainModal,
              openConnectModal,
              authenticationStatus,
              mounted
            }) => {
              // Note: If your app doesn't use authentication, you
              // can remove all 'authenticationStatus' checks
              const ready = mounted && authenticationStatus !== "loading";
              const connected =
                ready &&
                account &&
                chain &&
                (!authenticationStatus || authenticationStatus === "authenticated");

              return (
                <div
                  {...(!ready && {
                    "aria-hidden": true,
                    style: {
                      opacity: 0,
                      pointerEvents: "none",
                      userSelect: "none",
                      width: "100%"
                    }
                  })}
                >
                  {(() => {
                    if (!connected) {
                      return (
                        <Button
                          sx={{
                            color: "#FFF",
                            height: "48px",
                            width: ["300px", "516px", "516px"]
                          }}
                          onClick={openConnectModal}
                        >
                          Connect Wallet
                        </Button>
                      );
                    }

                    if (chain.unsupported) {
                      return (
                        <Button
                          sx={{
                            color: "#FFF",
                            height: "48px",
                            width: ["300px", "516px", "516px"]
                          }}
                          onClick={openChainModal}
                        >
                          Change Network
                        </Button>
                      );
                    }

                    return (
                      <div style={{ display: "flex", gap: 12 }}>
                        <button
                          onClick={openChainModal}
                          style={{ display: "flex", alignItems: "center" }}
                          type="button"
                        >
                          {chain.hasIcon && (
                            <div
                              style={{
                                background: chain.iconBackground,
                                width: 12,
                                height: 12,
                                borderRadius: 999,
                                overflow: "hidden",
                                marginRight: 4
                              }}
                            >
                              {chain.iconUrl && (
                                <img
                                  alt={chain.name ?? "Chain icon"}
                                  src={chain.iconUrl}
                                  style={{ width: 12, height: 12 }}
                                />
                              )}
                            </div>
                          )}
                          {chain.name}
                        </button>

                        <button onClick={openAccountModal} type="button">
                          {account.displayName}
                          {account.displayBalance ? ` (${account.displayBalance})` : ""}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              );
            }}
          </ConnectButton.Custom>
        </Flex>
      </Flex>
    </Flex>
  );
};
